import { gql, useQuery, useMutation } from '@apollo/client';
import { ReportDataType, ReportsBuilder } from '@baemingo/reports-builder';
import { useCurrency, useDates, useI18n, useSelector, useLogger } from '~Hooks';
import { GetOrders, GetVenueTableEvents } from './__queries__';
import { DateTime } from 'luxon';
import { aggregateCategorySales } from '~utilities';
import { GetCategoriesQuery_user_company_categories } from '../__queries__';
import { useEffect, useMemo } from 'react';
import { TableEvent } from '@baemingo/table-events';
import { UniverseBuilder as TableUniverse } from '@baemingo/table-events';
import _ from 'lodash';
import { getAggregatedCategoryRows, printReport, ReportRow } from '~Components/Reports/utils';
import { CountAndAmount } from '@baemingo/report-events/dist/types';

const ORDERS_QUERY = gql`
  query GetOrders(
    $venueIds: [String!]!
    $origins: [REPORTING_CHANNEL!]
    $startDate: DateTime!
    $endDate: DateTime!
    $limit: Int
    $lastKey: String
  ) {
    operativeViewer {
      id
      orders(
        venueIds: $venueIds
        origins: $origins
        createdAt_gt: $startDate
        createdAt_lt: $endDate
        limit: $limit
        lastKey: $lastKey
      )
    }
  }
`;

const TABLE_EVENTS_QUERY = gql`
  query GetVenueTableEvents($venueId: String!, $sentAfter: DateTime!, $lastKey: Json) {
    tableEvents(venueId: $venueId, sentAfter: $sentAfter, limit: 1000, lastKey: $lastKey) {
      lastKey
      items
    }
  }
`;

const GET_REPORT = gql`
  mutation GetReport(
    $venueId: String!
    $start: DateTime!
    $end: DateTime!
    $staffId: String
    $reportType: GENERATED_REPORT_TYPE!
  ) {
    generateReport(
      venueId: $venueId
      returnFullReport: true
      createdAt_gt: $start
      createdAt_lt: $end
      staffId: $staffId
      type: $reportType
      includeItems: true
    ) {
      success
      message
      error
      reportId
      report
    }
  }
`;

export const useReport = (venueId: string, chosenDate: Date) => {
  const { i18n } = useI18n();
  const dates = useDates();
  const currency = useCurrency();
  const logger = useLogger('use-report');

  const staffs = useSelector((state) => state.companyStaff?.staffs);

  const [getReportMutation, { data: reportData, loading: reportLoading }] = useMutation(
    GET_REPORT,
  );

  const ordersQuery = useQuery<GetOrders>(ORDERS_QUERY, {
    variables: {
      venueIds: [venueId],
      startDate: dates.getAccountingStartDate(chosenDate),
      endDate: dates.getAccountingEndDate(chosenDate),
      origins: ['POS'],
    },
    skip: !venueId,
    fetchPolicy: 'network-only',
  });

  const tableEventsQuery = useQuery<GetVenueTableEvents>(TABLE_EVENTS_QUERY, {
    variables: {
      venueId,
      sentAfter: dates.getAccountingStartDate(chosenDate),
    },
    skip: !venueId,
    fetchPolicy: 'network-only',
  });

  /* We can only fetch from a starting point, the endDate we need to manually adjust */
  useEffect(() => {
    if (!tableEventsQuery.data?.tableEvents) {
      return;
    }

    // Extract last event's timestamp
    const lastEventKey = tableEventsQuery.data.tableEvents.lastKey;

    if (!lastEventKey) {
      return; // No more events to fetch
    }

    const endDate = DateTime.fromISO(dates.getAccountingEndDate(chosenDate));
    const lastEventDate = DateTime.fromISO(lastEventKey.sentAt);

    if (lastEventDate >= endDate) {
      return; // Stop fetching if we passed the end date
    }

    // Fetch more events
    tableEventsQuery.fetchMore({
      query: TABLE_EVENTS_QUERY,
      variables: {
        venueId,
        sentAfter: lastEventKey.sentAt,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.tableEvents) return prev;

        return {
          ...prev,
          tableEvents: {
            ...prev.tableEvents,
            items: [...prev.tableEvents.items, ...fetchMoreResult.tableEvents.items],
            lastKey: fetchMoreResult.tableEvents.lastKey,
          },
        };
      },
    });
  }, [tableEventsQuery.data, chosenDate]);

  const events = useMemo(() => {
    if (!tableEventsQuery.data?.tableEvents?.items) {
      return [];
    }

    let tableEvents = tableEventsQuery.data.tableEvents.items as TableEvent[];

    const endDate = DateTime.fromISO(dates.getAccountingEndDate(chosenDate));

    tableEvents = tableEvents.filter((e) => DateTime.fromISO(e.createdAt) < endDate); // Filter out events past endDate

    return _.uniqBy(tableEvents, 'eventId');
  }, [tableEventsQuery.data, chosenDate]);

  const staffWithData = staffs?.filter((s) => {
    return (
      ordersQuery.data?.operativeViewer?.orders.some((o) => o.data.staffId === s.id) ||
      events.some((e) => e.staffId === s.id)
    );
  });

  const formatGeneratedReport = ({
    report,
    showCategorySales,
    allCategories,
    selectedStaffId,
    openCheques,
  }: {
    report: ReportDataType;
    showCategorySales: boolean;
    allCategories: GetCategoriesQuery_user_company_categories[];
    selectedStaffId?: string | null;
    openCheques?: CountAndAmount | null;
  }) => {
    const categoryRows = getAggregatedCategoryRows(
      aggregateCategorySales(allCategories, report.categoryTotals, i18n),
      currency.format,
      i18n,
    );

    const totalSales = [
      {
        leftText: i18n('Report.TotalSales'),
        rightText: i18n('Report.Sum'),
        isHeader: true,
      },
      {
        leftText: `${report.numberOfOrders}x ${i18n('Report.Orders')}`,
        rightText: currency.format(report.totalSales),
      },
    ];

    const categorySales = showCategorySales
      ? [
          {
            leftText: i18n('Report.CategorySales'),
            rightText: i18n('General.Sum'),
            isHeader: true,
          },
          ...categoryRows,
        ]
      : [];

    const VAT: ReportRow[] = [
      { leftText: i18n('Report.VAT'), rightText: i18n('Report.Sum'), isHeader: true },
      ...Object.keys(report.vatTotals).map((key) => {
        return {
          leftText: `${key}% ${i18n('Report.VAT')}`,
          rightText: currency.format(report.vatTotals[parseInt(key)] ?? undefined),
        };
      }),
      {
        leftText: i18n('Report.Total'),
        rightText: currency.format(report.vatTotalAmount),
        bold: true,
      },
    ];

    const net: ReportRow[] = [
      { leftText: i18n('Report.NetSales'), rightText: i18n('Report.Sum'), isHeader: true },
      ...Object.entries(report.saleForVatTotals || {}).map(([vatRate, saleForVatTotal]) => {
        const vatTotal = report.vatTotals[Number(vatRate)] || 0;
        const netIncome = saleForVatTotal - vatTotal;

        return {
          leftText: `${vatRate}% ${i18n('Report.NetIncome')}`,
          rightText: currency.format(netIncome),
        };
      }),
      {
        leftText: i18n('Report.Total'),
        rightText: currency.format(report.totalSales - report.vatTotalAmount),
        bold: true,
      },
    ];

    const tips: ReportRow[] = [
      {
        leftText: i18n('Report.Tips'),
        rightText: i18n('Report.Sum'),
        isHeader: true,
      },
      {
        leftText: i18n('Report.Tips'),
        rightText: currency.format(report.totalTips - report.tipRefunds.amount),
      },
    ];

    const discounts: ReportRow[] = [
      {
        leftText: i18n('Report.Discounts'),
        rightText: i18n('Report.Sum'),
        isHeader: true,
      },
      {
        leftText: `${report.discountsTotal}x ${i18n('Report.Discounts')}`,
        rightText: currency.format(report.discountsAmount),
      },
    ];

    const refunds: ReportRow[] = [
      {
        leftText: i18n('Report.Refunds'),
        rightText: i18n('Report.Sum'),
        isHeader: true,
      },
      {
        leftText: `${report.totalRefunds?.count ?? 0}x ${i18n('Report.OrderRefunds')}`,
        rightText: currency.format(report.totalRefunds?.amount ?? 0),
      },
      {
        leftText: `${report.tipRefunds?.count ?? 0}x ${i18n('ZReport.TipRefunds')}`,
        rightText: currency.format(report.tipRefunds?.amount ?? 0),
      },
    ];

    const openChequesRows: ReportRow[] =
      !selectedStaffId && openCheques
        ? [
            {
              leftText: i18n('Report.OpenCheques'),
              rightText: i18n('Report.Sum'),
              isHeader: true,
            },
            {
              leftText: `${openCheques.count}x`,
              rightText: currency.format(openCheques.amount),
            },
          ]
        : [];

    const removalDetails = (report as any).removalDetails || {};
    const corrections: ReportRow[] = selectedStaffId
      ? [
          { leftText: i18n('ZReport.Corrections'), rightText: i18n('Report.Sum'), isHeader: true },
          ...Object.values(removalDetails)
            .sort((a: any, b: any) => a.title.localeCompare(b.title))
            .map(({ id, title, count, totalAmount }: any) => ({
              leftText: count + 'x ' + title,
              rightText: currency.format(totalAmount),
            })),
          {
            leftText: `${Object.values(removalDetails).reduce(
              (sum: number, { count }: any) => sum + count,
              0,
            )}x ${i18n('Report.Total')}`,
            rightText: currency.format(
              Object.values(removalDetails).reduce(
                (sum: number, { totalAmount }: any) => sum + totalAmount,
                0,
              ),
            ),
            bold: true,
          },
        ]
      : [];

    const paymentMethodTotals: ReportRow[] = [
      {
        leftText: i18n('ZReport.PaymentMethods'),
        rightText: i18n('General.Sum'),
        isHeader: true,
      },
      ...Object.entries(report.paymentMethodTotals).map(([name, data]) => ({
        leftText: `${data.count}x ${name}`,
        rightText: currency.format(data.amount),
      })),
    ];

    const tipMethodTotals: ReportRow[] =
      Object.entries(report.tipsByPayment).length > 0
        ? [
            {
              leftText: i18n('Report.StaffReport.TipsByPayment'),
              rightText: i18n('General.Sum'),
              isHeader: true,
            },
            ...Object.entries(report.tipsByPayment).map(([name, amount]) => ({
              leftText: name,
              rightText: currency.format(amount),
            })),
          ]
        : [];

    const groupedItemsByCategory = _.groupBy(report.items, 'categoryId');

    // Only show item sales if it is the full report
    const itemSales: ReportRow[] = !selectedStaffId
      ? [
          {
            leftText: i18n('Report.DetailedSales'),
            rightText: i18n('General.Sum'),
            isHeader: true,
          },
          ...Object.entries(groupedItemsByCategory).flatMap(([categoryId, items]) => {
            const category = allCategories.find((c) => c.id === categoryId);

            // Category header row
            const categoryHeader: ReportRow = {
              leftText: category?.name || categoryId,
              rightText: currency.format(report.categoryTotals[categoryId].amount),
              bold: true,
            };

            // Item rows for each product in the category
            const itemRows: ReportRow[] = items.map((item) => {
              return {
                leftText: '    ' + item.count + 'x ' + item.title,
                rightText: currency.format(item.amount),
                secondaryText: `${item.count} x`,
              };
            });

            return [categoryHeader, ...itemRows];
          }),
        ]
      : [];

    const allRows: ReportRow[] = [
      ...totalSales,
      ...tips,
      ...discounts,
      ...refunds,
      ...openChequesRows,
      ...corrections,
      ...VAT,
      ...net,
      ...paymentMethodTotals,
      ...tipMethodTotals,
      ...categorySales,
      ...itemSales,
    ];

    return allRows;
  };

  const generateReport = async ({ isStaffReport }: { isStaffReport: boolean }) => {
    try {
      logger.info('Generating report', {
        venueId,
        date: chosenDate,
      });

      const result = await getReportMutation({
        variables: {
          venueId,
          start: dates.getAccountingStartDate(chosenDate),
          end: dates.getAccountingEndDate(chosenDate),
          reportType: isStaffReport ? 'STAFF' : 'SALES',
        },
      });

      if (result.data?.generateReport?.success) {
        logger.info('Report generated successfully', {
          date: chosenDate,
        });

        return result.data.generateReport.report;
      } else {
        logger.error('Failed to generate report', {
          date: chosenDate,
          message: result.data?.generateReport?.message,
        });
        return null;
      }
    } catch (error) {
      logger.error('Error generating report', {
        date: chosenDate,
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      return null;
    }
  };

  const printMultipleReports = async (
    allCategories: GetCategoriesQuery_user_company_categories[],
  ) => {
    if (!staffWithData) {
      logger.warn('No staff data available for printing multiple reports');
      return;
    }

    const allStaffReports = await generateReport({
      isStaffReport: true,
    });

    if (!allStaffReports) {
      logger.warn('No reports generated for any staff');
      return;
    }

    Object.entries(allStaffReports as Record<string, ReportDataType>).forEach(
      ([staffId, report]) => {
        const staff = staffs?.find((s) => s.id === staffId);
        if (!staff) {
          logger.warn('Staff not found for report', { staffId });
          return;
        }

        const formattedReport = formatGeneratedReport({
          report,
          showCategorySales: true,
          allCategories,
          selectedStaffId: staffId,
        });

        const title = staff.name;
        const date = DateTime.fromJSDate(chosenDate).toFormat('yyyy-MM-dd');
        printReport(title, formattedReport, i18n, date);

        logger.info('Printed report for staff', {
          staffId,
          staffName: staff.name,
          date,
        });
      },
    );
  };

  return {
    formatGeneratedReport,
    generateReport,
    printMultipleReports,
    loading: ordersQuery.loading || tableEventsQuery.loading,
    reportData,
    reportLoading,
    staffWithData: staffWithData ?? [],
  };
};
